import { useContext, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Formik } from 'formik'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { IconButton, Stack, TextField, Typography } from '@mui/material'
import { PersonRemoveOutlined } from '@mui/icons-material'
import AppFormContainer from 'components/forms/AppFormContainer'
import AppFormSection from 'components/forms/AppFormSection'
import useFieldStore from 'components/forms/useFieldStore'
import { wordingOf } from 'components/forms/refusal'
import { applyServerErrors } from 'components/forms/serverErrors'
import schema from 'schemas/firm'
import FetchContext from '../../context/fetch/FetchContext'
import Page from '../pages/Page'
import Section from '../containers/Section'
import PendingBadge from '../contacts/PendingBadge'
import useContactPageValidations from '../contacts/useContactPageValidations'
import useOptimisticList from '../contacts/useOptimisticList'
import { addressFieldLabelOf, detailFieldLabelOf } from '../contacts/contactLabels'
import { toAddressBody, toDetailBody } from '../contacts/contactLists'
import FirmContactSettings from './FirmContactSettings'
import RemoveUserDialog from './RemoveUserDialog'

const FIRMS_QUERY_KEY = ['firms']

const NAME_REFUSAL_CODE = 'FIRM_NAME_REQUIRED'

const DETAILS_LENS = {
    get: (cached) => cached?.data?.contact?.details ?? [],
    set: (cached, details) => ({
        ...cached,
        data: {
            ...cached.data,
            contact: {
                ...cached.data?.contact,
                details,
            },
        },
    }),
}

const ADDRESSES_LENS = {
    get: (cached) => cached?.data?.contact?.addresses ?? [],
    set: (cached, addresses) => ({
        ...cached,
        data: {
            ...cached.data,
            contact: {
                ...cached.data?.contact,
                addresses,
            },
        },
    }),
}

function nameRefusalWording(error) {
    const validations = error?.response?.data?.error?.details?.validations ?? []
    const refused = validations.find((validation) => validation.code === NAME_REFUSAL_CODE)

    return refused == null ? null : wordingOf(refused.code, refused.params)
}

function FirmNameForm({
    name,
    mailbox,
    store,
    onRefused,
}) {
    const formikRef = useRef(null)
    const { name: nameField } = schema.describe().fields
    const fields = { name: nameField }

    const fieldStore = useFieldStore({
        store,
        onRefused: (error) => {
            const wording = onRefused(error)
            if (wording == null) {
                return
            }

            applyServerErrors(formikRef.current, { name: wording })
        },
    })

    const heldName = useRef(name)
    if (!fieldStore.held) {
        heldName.current = name
    }

    return (
        <Formik
            innerRef={formikRef}
            onSubmit={() => {}}
            enableReinitialize={true}
            validationSchema={schema.pick(['name'])}
            initialValues={{ name: heldName.current }}
        >
            {(formik) => (
                <AppFormSection
                    title="פרטי המשרד"
                    section="basic"
                    fields={fields}
                    xs={12}
                    md={6}
                    pendingFields={fieldStore.pending}
                    onFieldFocus={(field) => fieldStore.onFocus(formik, field)}
                    onFieldBlur={(field) => fieldStore.onBlur(formik, field)}
                >
                    <TextField
                        id="firm-mailbox"
                        inputProps={{ 'data-testid': 'firm-mailbox' }}
                        label="תיבת דואר"
                        value={mailbox}
                        disabled
                        fullWidth
                    />
                </AppFormSection>
            )}
        </Formik>
    )
}

function FirmUsers({
    users,
    removingIds,
    onRemove,
}) {
    return (
        <Section title="משתמשים" xs={12} md={6}>
            <Stack spacing={1}>
                {users.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        לא שויכו משתמשים למשרד
                    </Typography>
                )}
                {users.map((user) => {
                    const removing = removingIds.includes(user.id)

                    return (
                        <Stack
                            key={user.id}
                            data-testid={`firm-user-${user.id}`}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                        >
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                {user.username} ({user.email})
                            </Typography>
                            {removing && <PendingBadge testId="firm-user-pending" />}
                            <IconButton
                                aria-label="הסרה"
                                data-testid={`firm-user-remove-${user.id}`}
                                disabled={removing}
                                onClick={() => onRemove(user)}
                            >
                                <PersonRemoveOutlined fontSize="small" />
                            </IconButton>
                        </Stack>
                    )
                })}
            </Stack>
        </Section>
    )
}

function FirmEditPage({ firmId }) {
    const fetcher = useContext(FetchContext)
    const queryClient = useQueryClient()

    const firmQueryKey = ['firms', firmId]
    const firmPath = `/firms/${firmId}`
    const settingsPath = `${firmPath}/settings`

    const [removingIds, setRemovingIds] = useState([])
    const [userToRemove, setUserToRemove] = useState(null)

    const { data: loaded, isFetching } = useQuery({
        queryKey: firmQueryKey,
        queryFn: () => fetcher.fetchFirm(firmId),
        enabled: fetcher.isAuthenticated(),
    })

    const firm = loaded?.data

    const { locked, showRefusal } = useContactPageValidations(loaded?.meta?.validations)

    const refetchFirm = () => queryClient.invalidateQueries({ queryKey: firmQueryKey })

    const nameMutation = useMutation({
        mutationFn: (changes) => fetcher.authenticatedFetch({
            method: 'patch',
            path: firmPath,
            data: { data: changes },
        }),
        onSuccess: async () => {
            await refetchFirm()
            queryClient.invalidateQueries({
                queryKey: FIRMS_QUERY_KEY,
                exact: true,
            })
        },
    })

    const removeUserMutation = useMutation({
        mutationFn: (userId) => fetcher.authenticatedFetch({
            method: 'delete',
            path: `${firmPath}/users/${userId}`,
        }),
        onSuccess: async () => {
            await refetchFirm()
            queryClient.invalidateQueries({
                queryKey: FIRMS_QUERY_KEY,
                exact: true,
            })
        },
        onError: (error) => showRefusal(error),
    })

    const detailsList = useOptimisticList({
        queryKey: firmQueryKey,
        lens: DETAILS_LENS,
        path: `${settingsPath}/details`,
        positionsPath: `${settingsPath}/detail-positions`,
        scope: `firm-${firmId}:details`,
        toInput: toDetailBody,
        labelOf: detailFieldLabelOf,
        onRefused: (error) => showRefusal(error, { labelOf: detailFieldLabelOf }),
    })

    const addressesList = useOptimisticList({
        queryKey: firmQueryKey,
        lens: ADDRESSES_LENS,
        path: `${settingsPath}/addresses`,
        positionsPath: `${settingsPath}/address-positions`,
        scope: `firm-${firmId}:addresses`,
        toInput: toAddressBody,
        labelOf: addressFieldLabelOf,
        onRefused: (error) => showRefusal(error, { labelOf: addressFieldLabelOf }),
    })

    const storeName = (changes) => nameMutation.mutateAsync(changes)

    const handleNameRefusal = (error) => {
        const fieldErrors = showRefusal(error)

        return fieldErrors.name ?? nameRefusalWording(error)
    }

    const confirmUserRemoval = () => {
        const user = userToRemove
        setUserToRemove(null)
        setRemovingIds((current) => [...current, user.id])

        removeUserMutation.mutate(user.id, {
            onSettled: () => {
                setRemovingIds((current) => current.filter((id) => id !== user.id))
            },
        })
    }

    const commands = [
        {
            scheme: 'link',
            icon: 'arrow_upward',
            to: '/firms',
            position: 'end',
            color: 'info',
        },
    ]

    return (
        <Page
            commands={commands}
            className="firm"
            title={firm?.name ?? 'משרד'}
            busy={isFetching}
        >
            <AppFormContainer>
                {firm != null && (
                    <FirmNameForm
                        name={firm.name ?? ''}
                        mailbox={firm.mailbox ?? ''}
                        store={storeName}
                        onRefused={handleNameRefusal}
                    />
                )}

                <FirmUsers
                    users={firm?.users ?? []}
                    removingIds={removingIds}
                    onRemove={setUserToRemove}
                />

                {firm != null && (
                    <FirmContactSettings
                        detailsList={detailsList}
                        addressesList={addressesList}
                        locked={locked}
                        consentPathOf={(detailId) => `${settingsPath}/details/${detailId}/consent`}
                        busy={isFetching}
                    />
                )}
            </AppFormContainer>
            <RemoveUserDialog
                user={userToRemove}
                onConfirm={confirmUserRemoval}
                onCancel={() => setUserToRemove(null)}
            />
        </Page>
    )
}

export default function FirmPage() {
    const { firmId } = useParams()

    return <FirmEditPage firmId={firmId} />
}
